import Phaser from 'phaser';

/**
 * The Moon's send-off — the cutscene that plays when Prince Nacho's second
 * form finally falls (spec
 * docs/superpowers/specs/2026-09-11-through-space-and-time-design.md §4).
 *
 * Ten seconds, full screen, on EVERY real win of Through Space and Time: the
 * ten seconds are the level's send-off, and beating it again replays them.
 * The Moon Stone is a one-time prize claimed in GameScene, so nothing in here
 * touches progress.
 *
 * Written as an overlay over the HOST scene, in the style of
 * `varietyOrbCutscene.ts`, rather than as a registered Phaser scene: it has to
 * hand control back to a Level Complete overlay that lives *inside*
 * GameScene, and a scene swap would tear that context down.
 *
 * The cast is drawn from the TOWER art — these are the towers the player
 * fielded, taking the moon back.
 */

/** Logical canvas. Everything below is authored against 1280 × 720. */
const W = 1280;
const H = 720;

/** Above every GameScene layer AND its win overlay (which sits at 50). */
const DEPTH = 500;

/**
 * Beat boundaries in ms. Ten seconds, and a skip does not jump past the end:
 * it runs the same hand-off on a short fade, so the scene always resolves the
 * same way rather than snapping to the result screen.
 */
const T_SHOOT = 2400;
const T_BLACK = 3200;
const T_SURFACE = 4000;
const T_DROP_2 = 5000;
const T_DROP_4 = 6000;
const T_VICTORY = 7000;
const T_TOTAL = 10000;

/** Where the moon's surface is — the landing line the cast drops onto. */
const GROUND_Y = 545;

/**
 * Who lands, and when. Bow Hambi first and alone (he fired the arrow), then
 * the Ninja and Shrekbook, then the other four together. Sensei Ketchup is
 * TIER 1 because the brief says so; the rest are tier 1 as well, which is the
 * silhouette every level notice already uses.
 */
const CAST: readonly { key: string; x: number; at: number }[] = [
  { key: 'tower-bow-1', x: 640, at: T_SURFACE },
  { key: 'tower-ninja-1', x: 500, at: T_DROP_2 },
  { key: 'tower-shrekbook-1', x: 780, at: T_DROP_2 },
  { key: 'tower-wise-1', x: 360, at: T_DROP_4 },
  { key: 'tower-sensei-1', x: 920, at: T_DROP_4 },
  { key: 'tower-blade-1', x: 230, at: T_DROP_4 },
  { key: 'tower-bomb-1', x: 1050, at: T_DROP_4 },
];

/**
 * Play the send-off. `done` is called exactly once, when the cutscene (or a
 * skip) finishes; the returned function cancels it — GameScene calls that on
 * shutdown so a player who leaves mid-cutscene is not left with orphan tweens.
 */
export function playPrinceNachoCutscene(scene: Phaser.Scene, done: () => void): () => void {
  const objs: Phaser.GameObjects.GameObject[] = [];
  const timers: Phaser.Time.TimerEvent[] = [];
  let finished = false;

  const add = <T extends Phaser.GameObjects.GameObject>(o: T): T => {
    objs.push(o);
    return o;
  };
  const later = (ms: number, fn: () => void) => {
    timers.push(scene.time.delayedCall(ms, fn));
  };
  const teardown = () => {
    timers.forEach((t) => t.remove());
    objs.forEach((o) => {
      scene.tweens.killTweensOf(o);
      o.destroy();
    });
    objs.length = 0;
  };
  const finish = () => {
    if (finished) return;
    finished = true;
    scene.input.off('pointerdown', skip);
    teardown();
    done();
  };

  // ── The stage: an opaque cover over the whole board ────────────────────────
  const cover = add(
    scene.add
      .rectangle(W / 2, H / 2, W, H, 0x05030c, 1)
      .setDepth(DEPTH)
      .setInteractive()
      .setName('prince-cutscene'),
  );
  // Stars, so the cover is a place rather than a hole. Deterministic, not
  // random: a cutscene that looks different every replay reads as a glitch.
  const stars = add(scene.add.graphics().setDepth(DEPTH + 1));
  for (let i = 0; i < 120; i++) {
    const x = (i * 977) % W;
    const y = (i * 613) % H;
    stars.fillStyle(0xffffff, 0.2 + ((i * 37) % 60) / 120);
    stars.fillCircle(x, y, 1 + ((i * 17) % 3) * 0.6);
  }

  // ── 0.0–2.4s: BOW HAMBI, huge, draws and looses an arrow ───────────────────
  const bow = scene.textures.exists('tower-bow-1')
    ? add(
        scene.add
          .image(W / 2, H / 2 + 20, 'tower-bow-1')
          .setDisplaySize(360, 360)
          .setDepth(DEPTH + 2)
          .setName('prince-cutscene-bow'),
      )
    : null;
  if (bow) {
    bow.setAlpha(0).setScale(bow.scaleX * 0.75);
    scene.tweens.add({ targets: bow, alpha: 1, duration: 500 });
    // He rears back to draw …
    scene.tweens.add({
      targets: bow,
      scaleX: bow.scaleX * 1.12,
      scaleY: bow.scaleY * 1.12,
      duration: 900,
      delay: 500,
      ease: 'Sine.easeInOut',
    });
  }
  const caption = add(
    scene.add
      // Low on the frame: the top band belongs to achievement toasts, and a
      // caption there is read through one of them.
      .text(W / 2, 640, 'One shot left.', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '34px',
        fontStyle: 'bold',
        color: '#ffd23f',
      })
      .setOrigin(0.5)
      .setDepth(DEPTH + 4)
      .setAlpha(0),
  );
  scene.tweens.add({ targets: caption, alpha: 1, duration: 600, delay: 400 });

  // … and looses it at 1.9s, so the arrow is gone by the cut to black.
  later(1900, () => {
    const arrow = add(
      scene.add
        .graphics()
        .setDepth(DEPTH + 3)
        .setName('prince-cutscene-arrow'),
    );
    const draw = (x: number) => {
      arrow.clear();
      arrow.lineStyle(6, 0xf0e2c0, 1);
      arrow.lineBetween(x - 70, H / 2 + 10, x + 40, H / 2 + 10);
      arrow.fillStyle(0xd7d7e0, 1);
      arrow.fillTriangle(x + 40, H / 2 - 4, x + 40, H / 2 + 24, x + 76, H / 2 + 10);
    };
    let x = W / 2;
    draw(x);
    const fly = scene.time.addEvent({
      delay: 16,
      loop: true,
      callback: () => {
        x += 46;
        draw(x);
        if (x > W + 120) fly.remove();
      },
    });
    timers.push(fly);
    if (bow) {
      // The release: a short snap forward.
      scene.tweens.add({
        targets: bow,
        scaleX: bow.scaleX * 0.94,
        scaleY: bow.scaleY * 0.94,
        duration: 120,
        yoyo: true,
      });
    }
  });

  // ── 2.4–3.2s: the screen turns black ──────────────────────────────────────
  later(T_SHOOT, () => {
    scene.tweens.add({
      targets: [bow, caption, stars].filter(Boolean),
      alpha: 0,
      duration: T_BLACK - T_SHOOT,
    });
  });

  // ── 3.2s: the moon's surface ──────────────────────────────────────────────
  later(T_BLACK, () => {
    const ground = add(
      scene.add
        .graphics()
        .setDepth(DEPTH + 1)
        .setAlpha(0),
    );
    // A pale grey regolith plain under a black sky, with a few craters. Drawn
    // rather than generated: it is on screen for six seconds and needs no key.
    ground.fillStyle(0x8d8b96, 1);
    ground.fillRect(0, GROUND_Y + 40, W, H - GROUND_Y - 40);
    ground.fillStyle(0x7a7885, 1);
    for (let i = 0; i < 9; i++) {
      const cx = 70 + i * 145;
      ground.fillEllipse(cx, GROUND_Y + 80 + ((i * 53) % 60), 90 - ((i * 13) % 40), 22);
    }
    // A distant blue-white Earth, low on the left, because this is the moon.
    ground.fillStyle(0x2f6fd0, 0.9);
    ground.fillCircle(150, 250, 60);
    ground.fillStyle(0x4ea36a, 0.8);
    ground.fillCircle(132, 236, 22);
    ground.fillCircle(168, 268, 16);
    scene.tweens.add({ targets: ground, alpha: 1, duration: 500 });
    // The stars come back over the new sky.
    stars.setAlpha(0);
    scene.tweens.add({ targets: stars, alpha: 1, duration: 500 });
  });

  // ── 3.2–7.0s: the cast falls in, in three waves ───────────────────────────
  for (const c of CAST) {
    later(c.at, () => {
      if (!scene.textures.exists(c.key)) return;
      const img = add(
        scene.add
          .image(c.x, -120, c.key)
          .setDisplaySize(120, 120)
          .setDepth(DEPTH + 3)
          .setName(`prince-cutscene-${c.key}`),
      );
      // Swiftly, per the brief: a fast drop with a hard landing.
      scene.tweens.add({
        targets: img,
        y: GROUND_Y,
        duration: 420,
        ease: 'Cubic.easeIn',
        onComplete: () => {
          // Dust on impact.
          const dust = add(scene.add.graphics().setDepth(DEPTH + 2));
          dust.fillStyle(0xd8d5e0, 0.55);
          dust.fillEllipse(c.x, GROUND_Y + 58, 130, 22);
          scene.tweens.add({ targets: dust, alpha: 0, duration: 600 });
          scene.tweens.add({
            targets: img,
            scaleY: img.scaleY * 0.86,
            duration: 90,
            yoyo: true,
            ease: 'Sine.easeOut',
          });
        },
      });
    });
  }

  // ── 7.0–10.0s: VICTORY, in large yellow text, under confetti ──────────────
  later(T_VICTORY, () => {
    const word = add(
      scene.add
        .text(W / 2, 300, 'VICTORY', {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '150px',
          fontStyle: 'bold',
          color: '#ffd23f',
          stroke: '#3a2a00',
          strokeThickness: 12,
        })
        .setOrigin(0.5)
        .setDepth(DEPTH + 6)
        .setName('prince-cutscene-victory')
        .setScale(0.5)
        .setAlpha(0),
    );
    scene.tweens.add({ targets: word, alpha: 1, scale: 1, duration: 420, ease: 'Back.easeOut' });

    // Confetti raining down for the three seconds the word is up.
    const bits = add(scene.add.graphics().setDepth(DEPTH + 5));
    const COLORS = [0xffd23f, 0xff5a7a, 0x4fd1ff, 0x7ce38b, 0xc77dff, 0xffffff];
    const flakes = Array.from({ length: 110 }, (_, i) => ({
      x: (i * 149) % W,
      y: -((i * 71) % H) - 20,
      vy: 130 + ((i * 37) % 120),
      w: 6 + ((i * 11) % 7),
      c: COLORS[i % COLORS.length],
      sway: ((i * 29) % 40) - 20,
    }));
    let last = scene.time.now;
    const fall = scene.time.addEvent({
      delay: 16,
      loop: true,
      callback: () => {
        const now = scene.time.now;
        const dt = Math.min(0.05, (now - last) / 1000);
        last = now;
        bits.clear();
        for (const f of flakes) {
          f.y += f.vy * dt;
          if (f.y > H + 20) f.y = -20;
          const x = f.x + Math.sin((now + f.x * 7) / 420) * f.sway;
          bits.fillStyle(f.c, 0.95);
          bits.fillRect(x, f.y, f.w, f.w * 1.6);
        }
      },
    });
    timers.push(fall);
  });

  // ── 10.0s: hand over to the Level Complete screen ─────────────────────────
  later(T_TOTAL, finish);

  // A tap skips straight to the hand-off — the same resolution, sooner.
  const skip = () => finish();
  cover.on('pointerdown', skip);
  scene.input.on('pointerdown', skip);

  return () => {
    if (finished) return;
    finished = true;
    scene.input.off('pointerdown', skip);
    teardown();
  };
}
